<template>
  <themed-modal
    name="site-modal"
    :title="site ? site.name : 'Site Details'"
    size="lg"
    :show="show"
    @close="handleClose"
    @opened="handleOpened"
  >
    <template #body>
      <div v-if="loading" class="site-modal__loading">
        <div class="site-modal__spinner"></div>
        <p>Loading site information...</p>
      </div>
      
      <div v-else-if="error" class="site-modal__error">
        <theme-icon icon="alert-triangle" type="icons" />
        <p>{{ error }}</p>
      </div>
      
      <div v-else-if="site" class="site-modal__details">
        <div class="site-modal__detail-row">
          <span class="site-modal__detail-label">Address:</span>
          <span class="site-modal__detail-value">{{ site.full_address || formatAddress(site) }}</span>
        </div>
        
        <div class="site-modal__detail-row">
          <span class="site-modal__detail-label">Subsidiary:</span>
          <span class="site-modal__detail-value">{{ site.subsidiary?.name || '-' }}</span>
        </div>
        
        <div class="site-modal__detail-row">
          <span class="site-modal__detail-label">Building:</span>
          <span class="site-modal__detail-value">{{ site.building?.address_line_1 || '-' }}</span>
        </div>
        
        <div class="site-modal__detail-row">
          <span class="site-modal__detail-label">Lease/Own:</span>
          <span class="site-modal__detail-value">{{ site.lease_or_own || '-' }}</span>
        </div>
        
        <div v-if="site.lease_cost" class="site-modal__detail-row">
          <span class="site-modal__detail-label">Lease Cost:</span>
          <span class="site-modal__detail-value">{{ formatCurrency(site.lease_cost) }}</span>
        </div>
        
        <div v-if="site.square_footage" class="site-modal__detail-row">
          <span class="site-modal__detail-label">Square Footage:</span>
          <span class="site-modal__detail-value">{{ formatNumber(site.square_footage) }}</span>
        </div>
        
        <div v-if="site.notes" class="site-modal__detail-row site-modal__detail-row--full">
          <span class="site-modal__detail-label">Notes:</span>
          <span class="site-modal__detail-value">{{ site.notes }}</span>
        </div>
      </div>
    </template>
    
    <template #footer>
      <div class="site-modal__actions">
        <button 
          type="button"
          class="site-modal__button site-modal__button--secondary"
          @click="handleClose"
          aria-label="Close modal"
        >
          Close
        </button>
        <button 
          v-if="site"
          type="button"
          class="site-modal__button site-modal__button--primary"
          @click="editSite"
          aria-label="Edit site"
        >
          Edit Site
        </button>
      </div>
    </template>
  </themed-modal>
</template>

<script>
export default {
  name: 'SiteModal',
  
  props: {
    siteId: {
      type: [Number, String],
      default: null
    },
    show: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      site: null,
      loading: false,
      error: null
    };
  },
  
  watch: {
    show(newValue) {
      if (newValue && this.siteId) {
        this.fetchSite();
      }
    },
    
    siteId(newValue) {
      if (this.show && newValue) {
        this.fetchSite();
      }
    }
  },
  
  methods: {
    async fetchSite() {
      if (!this.siteId) {
        return;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        const response = await axios.get(`/api/sites/${this.siteId}`);
        this.site = response.data;
      } catch (error) {
        this.error = 'Failed to load site information';
        console.error('Error fetching site:', error);
      } finally {
        this.loading = false;
      }
    },
    
    handleClose() {
      this.$emit('close');
    },
    
    handleOpened() {
      // Fetch site data when modal opens if not already loaded
      if (!this.site && this.siteId) {
        this.fetchSite();
      }
    },
    
    editSite() {
      if (this.site && this.site.id) {
        this.$router.push({ name: 'sites.edit', params: { id: this.site.id } });
        this.handleClose();
      }
    },
    
    formatAddress(site) {
      if (!site) return '-';
      
      const parts = [];
      if (site.building?.address_line_1) parts.push(site.building.address_line_1);
      if (site.building?.city) parts.push(site.building.city);
      if (site.building?.state) parts.push(site.building.state);
      if (site.building?.postal_code) parts.push(site.building.postal_code);
      
      return parts.length > 0 ? parts.join(', ') : '-';
    },
    
    formatNumber(value) {
      return value ? value.toLocaleString() : '-';
    },
    
    formatCurrency(value) {
      return value ? `$${value.toLocaleString()}` : '-';
    }
  }
};
</script>
